/**
 * Normalises a ScheduleInterviewPayload into a flat ChangeSnapshot
 * used for diffing original vs current state when editing an interview.
 *
 * Pure function — no hooks, no side effects.
 */
import type { EmailCommConfigPayload } from '@/api/useJobStageActions/useCreateJobStageAction';
import type { ScheduleInterviewPayload } from '../config/types';

/**
 * The API-hydrated shape (fromEditApi.ts) uses backend key names:
 *   from_user_id / cc_user_ids / bcc_user_ids
 * The form-synced shape (emailCompositionToCommConfig) uses frontend key names:
 *   from / cc / bcc
 * Both can appear in payloadRef.communication at different points in the
 * lifecycle, so normalizers must handle either variant.
 */
type EmailConfigShape = EmailCommConfigPayload & {
  from_user_id?: number;
  cc_user_ids?: number[];
  bcc_user_ids?: number[];
};

export interface ChangeSnapshot {
  // Details fields
  interviewName: unknown;
  interview_type: unknown;
  duration: unknown;
  start_time: unknown;
  end_time: unknown;
  location: unknown;
  calendar_visibility: unknown;
  enable_note_taker: unknown;
  participant_ids: string[];
  host_id: unknown;
  meeting_room_ids: string[];
  candidate_phone: unknown;
  // Calendar invite bodies
  candidate_calendar_body: unknown;
  interviewer_calendar_body: unknown;
  // Candidate email — all fields trigger resend_email_to_candidate
  candidate_email_enabled: unknown;
  candidate_ics_enabled: unknown;
  candidate_email_from: string | null;
  candidate_email_cc: string[];
  candidate_email_bcc: string[];
  candidate_email_subject: unknown;
  candidate_email_body: unknown;
  candidate_email_attachments: string[];
  // Interviewer email — all fields trigger resend_email_to_interviewer
  interviewer_email_enabled: unknown;
  interviewer_ics_enabled: unknown;
  interviewer_email_from: string | null;
  interviewer_email_cc: string[];
  interviewer_email_bcc: string[];
  interviewer_email_subject: unknown;
  interviewer_email_body: unknown;
  interviewer_email_attachments: string[];
}

/** Normalise IDs to a sorted string array regardless of whether the form
 *  stored a scalar (one_on_one) or array (panel / self-schedule). */
function normalizeIds(val: unknown): string[] {
  if (Array.isArray(val)) return val.map(String).sort();
  if (typeof val === 'string' && val) return [val];
  return [];
}

/** Sender: handles both hydrated key (from_user_id) and synced key (from). */
function normalizeFrom(cfg: EmailConfigShape | undefined): string | null {
  if (!cfg) return null;
  const id = cfg.from ?? cfg.from_user_id;
  if (typeof id === 'number') return String(id);
  return null;
}

/** Recipients: handles both hydrated keys (cc_user_ids/bcc_user_ids) and synced keys (cc/bcc). */
function normalizeRecipients(
  synced: number[] | undefined,
  hydrated: number[] | undefined
): string[] {
  const arr = synced ?? hydrated;
  if (!arr || !arr.length) return [];
  return arr.map(String).sort();
}

/** Normalise attachments: existing IDs (numbers) and new Files (by name+size). */
function normalizeAttachments(
  val: EmailCommConfigPayload['attachments']
): string[] {
  if (!val || !val.length) return [];
  return val
    .map((a) => {
      if (typeof a === 'number') return String(a);
      return `file:${a.name}:${a.size}`;
    })
    .sort();
}

export function buildChangeSnapshot(
  payload: ScheduleInterviewPayload
): ChangeSnapshot {
  const d = payload.details;
  const comm = payload.communication as Record<string, unknown>;

  const candCal = comm.calendar_invite_candidate as
    | { body?: unknown }
    | undefined;
  const intCal = comm.calendar_invite_interviewer as
    | { body?: unknown }
    | undefined;
  const candEmail = comm.candidate_email_comm_config as
    | EmailConfigShape
    | undefined;
  const intEmail = comm.interviewer_email_comm_config as
    | EmailConfigShape
    | undefined;

  // Use bookedSlot (set by hydrateDetailsFromApi from the API ISO datetime)
  // instead of startTime/endTime which are form-display strings only set
  // after the Details step syncs — bookedSlot is stable across both snapshots.
  const bookedSlot = d.bookedSlot as
    | { start?: string; end?: string }
    | undefined;

  const snapshot: ChangeSnapshot = {
    interviewName: d.interviewName ?? null,
    interview_type: d.interview_type ?? null,
    duration: d.duration ?? null,
    start_time: bookedSlot?.start ?? null,
    end_time: bookedSlot?.end ?? null,
    location: d.location ?? null,
    calendar_visibility: d.calendar_visibility ?? null,
    enable_note_taker: d.enable_note_taker ?? null,
    // Normalised — original is always string[], form may return scalar string
    participant_ids: normalizeIds(d.participant_ids),
    host_id: d.host_id || null,
    candidate_phone: d.candidate_phone ?? null,
    // Normalised — original is string[], form single-select may return scalar
    meeting_room_ids: normalizeIds(d.meeting_room_ids),
    candidate_calendar_body: candCal?.body ?? null,
    interviewer_calendar_body: intCal?.body ?? null,
    // Candidate email — full config snapshot
    candidate_email_enabled: comm.send_email_to_candidate ?? null,
    candidate_ics_enabled: comm.send_ics_to_candidate ?? null,
    candidate_email_from: normalizeFrom(candEmail),
    candidate_email_cc: normalizeRecipients(
      candEmail?.cc,
      candEmail?.cc_user_ids
    ),
    candidate_email_bcc: normalizeRecipients(
      candEmail?.bcc,
      candEmail?.bcc_user_ids
    ),
    candidate_email_subject: candEmail?.subject ?? null,
    candidate_email_body: candEmail?.body ?? null,
    candidate_email_attachments: normalizeAttachments(candEmail?.attachments),
    // Interviewer email — full config snapshot
    interviewer_email_enabled: comm.send_email_to_interviewer ?? null,
    interviewer_ics_enabled: comm.send_ics_to_interviewer ?? null,
    interviewer_email_from: normalizeFrom(intEmail),
    interviewer_email_cc: normalizeRecipients(
      intEmail?.cc,
      intEmail?.cc_user_ids
    ),
    interviewer_email_bcc: normalizeRecipients(
      intEmail?.bcc,
      intEmail?.bcc_user_ids
    ),
    interviewer_email_subject: intEmail?.subject ?? null,
    interviewer_email_body: intEmail?.body ?? null,
    interviewer_email_attachments: normalizeAttachments(intEmail?.attachments),
  };

  return snapshot;
}

export function snapshotFieldChanged(
  original: ChangeSnapshot,
  current: ChangeSnapshot,
  field: keyof ChangeSnapshot
): boolean {
  return (
    JSON.stringify(original[field] ?? null) !==
    JSON.stringify(current[field] ?? null)
  );
}
